export type Brand = 'start' | 'sttart';

export type BrandAssetType = 'logo' | 'icon' | 'white-logo' | 'white-icon';

export type BrandIconType = 'icon' | 'maskable-icon' | 'ios/apple-touch-icon';

export type BrandConfing = {
  brand: Brand;
  name: string;
  shortName: string;
  description: string;
  brandColor: string;
  backgroundColor: string;
  features: {
    signUp?: boolean;
    clientArea?: boolean;
    requiresDocument?: boolean;
    deposit?: boolean;
    issuePurchaseContract?: boolean;
  };
};

const BRAND_CONFIGS: Record<Brand, BrandConfing> = {
  start: {
    brand: 'start',
    name: 'Start',
    shortName: 'Start',
    description: 'Dinheiro veloz, porque o futuro já chegou para nós.',
    brandColor: '#cb242a',
    backgroundColor: '#fff',
    features: {
      signUp: false,
      clientArea: false,
      requiresDocument: false,
      deposit: false,
      issuePurchaseContract: true,
    },
  },
  sttart: {
    brand: 'sttart',
    name: 'Sttart',
    shortName: 'Sttart',
    description: 'Dinheiro veloz, porque o futuro já chegou para nós.',
    brandColor: '#d52b1e',
    backgroundColor: '#ffffff',
    features: {
      signUp: true,
      clientArea: true,
      requiresDocument: true,
      deposit: true,
      issuePurchaseContract: false,
    },
  },
};

export function getBrand(): Brand {
  // Using process env directly to avoid issues with env import in email templates and tailwind config
  const brand = process.env.NEXT_PUBLIC_BRAND;
  if (brand === 'sttart' || brand === 'start') return brand;
  return 'start';
}

/**
 * Brand-specific app configuration
 */
export function getBrandConfig() {
  const brand = getBrand();
  return BRAND_CONFIGS[brand];
}

/**
 * Resolves brand-specific asset paths based on the current brand
 */
export function getBrandAsset(assetType: BrandAssetType, format: 'svg' | 'png' = 'svg'): string {
  const brand = getBrand();
  const extension = format === 'svg' ? 'svg' : 'png';

  // Map asset types to their file names
  const assetMap: Record<BrandAssetType, string> = {
    logo: `logo.${extension}`,
    icon: `icon.${extension}`,
    'white-logo': `white-logo.${extension}`,
    'white-icon': `white-icon.${extension}`,
  };

  return `/brands/${brand}/${assetMap[assetType]}`;
}

/**
 * Gets brand-specific PWA icons path
 */
export function getBrandIconsPath(): string {
  const brand = getBrand();
  return `/brands/${brand}/icons`;
}

/**
 * Resolves brand-specific icon with size
 */
export function getBrandIcon(size: number, type: BrandIconType = 'icon'): string {
  const iconsPath = getBrandIconsPath();
  return `${iconsPath}/${type}-${size}x${size}.png`;
}

/**
 * Gets brand-specific favicon path
 */
export function getBrandFavicon(): string {
  const brand = getBrand();
  return `/brands/${brand}/favicon.ico`;
}

export type Icon = {
  src: string;
  type?: string | undefined;
  sizes?: string | undefined;
  purpose?: 'any' | 'maskable' | 'monochrome' | undefined;
};

/**
 * Gets all available icon sizes for PWA manifest
 */
export function getBrandIconSizes(): Array<Icon> {
  const standardSizes = [16, 32, 48, 192];
  const purposeAnySizes = [512];
  const maskableSizes = [72, 96, 128, 144, 152, 384, 512, 1024];
  const iOSSizes = [57, 72, 76, 114, 120, 144, 152, 180];

  const standardIcons = standardSizes.map((size) => ({
    src: getBrandIcon(size, 'icon'),
    sizes: `${size}x${size}`,
    type: 'image/png',
  }));

  const purposeAnyIcons = purposeAnySizes.map((size) => ({
    src: getBrandIcon(size, 'icon'),
    sizes: `${size}x${size}`,
    type: 'image/png',
    purpose: 'any',
  }));

  const maskableIcons = maskableSizes.map((size) => ({
    src: getBrandIcon(size, 'maskable-icon'),
    sizes: `${size}x${size}`,
    type: 'image/png',
    purpose: 'maskable',
  }));

  const iOSIcons = iOSSizes.map((size) => ({
    src: getBrandIcon(size, 'ios/apple-touch-icon'),
    sizes: `${size}x${size}`,
    type: 'image/png',
  }));

  return [...standardIcons, ...purposeAnyIcons, ...maskableIcons, ...iOSIcons];
}

/**
 * Gets brand-specific favicon links for HTML head
 */
export function getBrandFaviconLinks(): Array<{
  rel: string;
  href: string;
  sizes?: string;
  type?: string;
}> {
  return [
    {
      rel: 'icon',
      href: getBrandIcon(16),
      sizes: '16x16',
      type: 'image/png',
    },
    {
      rel: 'icon',
      href: getBrandIcon(32),
      sizes: '32x32',
      type: 'image/png',
    },
    {
      rel: 'apple-touch-icon',
      href: getBrandIcon(192),
      sizes: '192x192',
    },
    {
      rel: 'icon',
      href: getBrandFavicon(),
    },
  ];
}

/**
 * Generates a branded page title
 *
 * @param title Optional specific page title
 */
export function brandedPageTitle(title?: string) {
  const brandConfig = getBrandConfig();
  if (title) {
    return `${title} | ${brandConfig.name}`;
  }
  return brandConfig.name;
}
